import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/firebaseConfig';
import { useMarketplace } from '../../context/MarketplaceContext';
import { useCoins } from '../../context/CoinContext';
import { useAuth } from '../../context/AuthContext';
import { isMarketplaceUser } from '../../utils/roleManager';
import { checkCoinRequirement, calculateCartFees } from '../../utils/marketplaceFees';
import PaymentMethodsDisplay from './PaymentMethodsDisplay';
import CartProgressBar from './CartProgressBar';
import PaymentProofViewer from './PaymentProofViewer';
import { compressImage } from '../../utils/imageUtils';

const Cart = () => {
  const { t } = useTranslation();
  const { cart, removeFromCart, updateCartQuantity: updateCartQuantityContext, clearCart, getCartTotal, createOrder } = useMarketplace();
  
  const updateCartQuantity = async (productId, quantity) => {
    try {
      await updateCartQuantityContext(productId, quantity);
    } catch (error) {
      alert(error.message);
    }
  };
  const { coins } = useCoins();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // Step 1-5
  const [shippingAddress, setShippingAddress] = useState('');
  const [selectedItems, setSelectedItems] = useState(new Set()); // Selected product IDs
  const [itemPaymentMethods, setItemPaymentMethods] = useState({}); // Payment method per item
  const [itemShippingMethods, setItemShippingMethods] = useState({}); // Shipping method per item
  const [itemPaymentProofs, setItemPaymentProofs] = useState({}); // Payment proof per item
  const [itemShippingAddresses, setItemShippingAddresses] = useState({}); // Shipping address per group
  const [itemBuyerPhones, setItemBuyerPhones] = useState({}); // Buyer phone per group
  const [stores, setStores] = useState({}); // Store data by storeId
  const [groupedItems, setGroupedItems] = useState({}); // Items grouped by store and payment method

  const cartTotal = getCartTotal();

  // Load store information for cart items
  useEffect(() => {
    const loadStores = async () => {
      const storeIds = [...new Set(cart.map(item => item.storeId).filter(Boolean))];
      const storesData = {};
      
      for (const storeId of storeIds) {
        try {
          const storeRef = doc(db, 'stores', storeId);
          const storeSnap = await getDoc(storeRef);
          if (storeSnap.exists()) {
            storesData[storeId] = { id: storeSnap.id, ...storeSnap.data() };
          }
        } catch (error) {
          console.error('Error loading store:', error);
        }
      }
      
      setStores(storesData);
    };

    if (cart.length > 0) {
      loadStores();
    }
  }, [cart]);

  // Initialize selected items (all selected by default)
  useEffect(() => {
    if (cart.length > 0 && selectedItems.size === 0) {
      setSelectedItems(new Set(cart.map(item => item.productId)));
      // Initialize payment methods
      const initialPaymentMethods = {};
      cart.forEach(item => {
        initialPaymentMethods[item.productId] = 'direct';
      });
      setItemPaymentMethods(initialPaymentMethods);
    }
  }, [cart]);

  // Group items by store and payment method
  useEffect(() => {
    if (cart.length === 0 || selectedItems.size === 0) {
      setGroupedItems({});
      return;
    }

    const grouped = {};
    cart
      .filter(item => selectedItems.has(item.productId))
      .forEach(item => {
        const storeId = item.storeId || 'unknown';
        const paymentMethod = itemPaymentMethods[item.productId] || 'direct';
        const key = `${storeId}_${paymentMethod}`;
        
        if (!grouped[key]) {
          grouped[key] = {
            storeId,
            store: stores[storeId],
            paymentMethod,
            items: []
          };
        }
        grouped[key].items.push(item);
      });
    
    setGroupedItems(grouped);
  }, [cart, selectedItems, itemPaymentMethods, stores]);

  // Get selected items total
  const getSelectedTotal = () => {
    const selected = cart.filter(item => selectedItems.has(item.productId));
    return calculateCartFees(selected);
  };

  const selectedTotal = getSelectedTotal();

  const toggleItemSelection = (productId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedItems(newSelected);
  };

  const toggleAllSelection = () => {
    if (selectedItems.size === cart.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(cart.map(item => item.productId)));
    }
  };

  const handlePaymentProofChange = async (e, productId) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('กรุณาเลือกไฟล์รูปภาพ');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('ไฟล์ขนาดใหญ่เกินไป (สูงสุด 5MB)');
      return;
    }

    try {
      setLoading(true);
      // Compress image
      const compressedFile = await compressImage(file);
      
      // Upload to Firebase Storage
      const fileName = `payment-proof-${Date.now()}-${file.name}`;
      const storageRef = ref(storage, `payment-proofs/${currentUser.uid}/${fileName}`);
      await uploadBytes(storageRef, compressedFile);
      const url = await getDownloadURL(storageRef);
      
      setItemPaymentProofs(prev => ({ ...prev, [productId]: url }));
    } catch (error) {
      console.error('Error uploading payment proof:', error);
      alert('เกิดข้อผิดพลาดในการอัพโหลดไฟล์');
    } finally {
      setLoading(false);
    }
  };

  // Step validation functions
  const validateStep = (step) => {
    switch (step) {
      case 1: // Select Items
        if (selectedItems.size === 0) {
          alert(t('cart.selectItems'));
          return false;
        }
        return true;
      
      case 2: // Payment Method
        for (const item of cart.filter(i => selectedItems.has(i.productId))) {
          if (!itemPaymentMethods[item.productId]) {
            alert(`${t('cart.selectPaymentMethod')}: ${item.productName}`);
            return false;
          }
        }
        return true;
      
      case 3: // Payment Proof
        for (const [key, group] of Object.entries(groupedItems)) {
          if (group.paymentMethod === 'direct') {
            const productId = group.items[0]?.productId;
            if (!itemPaymentProofs[productId]) {
              alert(`กรุณาแนบหลักฐานการโอนสำหรับร้าน ${group.store?.name || 'ไม่ทราบ'}`);
              return false;
            }
          }
        }
        return true;
      
      case 4: // Shipping
        for (const [key, group] of Object.entries(groupedItems)) {
          const shippingAddress = itemShippingAddresses[key];
          const shippingMethod = itemShippingMethods[group.items[0]?.productId];
          
          if (!shippingAddress?.trim()) {
            alert(`กรุณาใส่ที่อยู่จัดส่งสำหรับร้าน ${group.store?.name || 'ไม่ทราบ'}`);
            return false;
          }
          
          if (!shippingMethod) {
            alert(`กรุณาเลือกวิธีการจัดส่งสำหรับร้าน ${group.store?.name || 'ไม่ทราบ'}`);
            return false;
          }
        }
        return true;
      
      case 5: // Confirm
        // Final validation before checkout
        return true;
      
      default:
        return true;
    }
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 5) {
        setCurrentStep(currentStep + 1);
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleBackStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleStepClick = (step) => {
    // Allow clicking on completed steps or current step
    if (step <= currentStep) {
      setCurrentStep(step);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCheckout = async () => {
    if (!currentUser) {
      alert('กรุณาเข้าสู่ระบบ');
      navigate('/login');
      return;
    }

    if (!isMarketplaceUser(currentUser)) {
      alert('คุณไม่มีสิทธิ์เข้าถึง Marketplace กรุณาเข้าสู่ระบบด้วยบัญชี Marketplace');
      return;
    }

    if (selectedItems.size === 0) {
      alert('กรุณาเลือกสินค้าที่ต้องการสั่งซื้อ');
      return;
    }

    // Validate shipping addresses, methods, and buyer phones for each group
    for (const [key, group] of Object.entries(groupedItems)) {
      const shippingAddress = itemShippingAddresses[key];
      const shippingMethod = itemShippingMethods[group.items[0]?.productId];
      const buyerPhone = itemBuyerPhones[key];
      
      if (!shippingAddress?.trim()) {
        alert(`กรุณาใส่ที่อยู่จัดส่งสำหรับร้าน ${group.store?.name || 'ไม่ทราบ'}`);
        return;
      }
      
      if (!shippingMethod) {
        alert(`กรุณาเลือกวิธีการจัดส่งสำหรับร้าน ${group.store?.name || 'ไม่ทราบ'}`);
        return;
      }
      
      if (!buyerPhone?.trim()) {
        alert(`กรุณาใส่เบอร์โทรผู้รับสำหรับร้าน ${group.store?.name || 'ไม่ทราบ'}`);
        return;
      }
    }

    // Check coins for all orders
    let totalFee = 0;
    for (const [key, group] of Object.entries(groupedItems)) {
      const groupTotal = calculateCartFees(group.items);
      const adminFee = group.paymentMethod === 'admin' ? Math.ceil(groupTotal.subtotal * 0.05) : 0;
      totalFee += groupTotal.fee + adminFee;
    }

    const feeCheck = checkCoinRequirement(coins, 'ORDER_FEE', { orderTotal: selectedTotal.subtotal });
    if (!feeCheck.hasEnough) {
      alert(feeCheck.message);
      return;
    }

    setLoading(true);
    try {
      const orderIds = [];
      
      // Create separate orders for each group
      for (const [key, group] of Object.entries(groupedItems)) {
        const groupTotal = calculateCartFees(group.items);
        const adminFee = group.paymentMethod === 'admin' ? Math.ceil(groupTotal.subtotal * 0.05) : 0;
        const shippingAddress = itemShippingAddresses[key];
        const shippingMethod = itemShippingMethods[group.items[0]?.productId];
        const buyerPhone = itemBuyerPhones[key];
        const paymentProof = itemPaymentProofs[group.items[0]?.productId] || null;
        
        // Create order with only items from this group
        const orderId = await createOrder({
          items: group.items, // Only items in this group
          shippingAddress,
          shippingMethod,
          buyerPhone,
          paymentMethod: group.paymentMethod === 'admin' ? 'admin_middleman' : 'direct',
          paymentProof,
          adminFee
        });
        
        orderIds.push(orderId);
      }
      
      alert(`${t('cart.orderCreated')} ${orderIds.length} ${t('cart.orderCreatedMessage')}`);
      navigate(`/marketplace/orders`);
    } catch (error) {
      alert('เกิดข้อผิดพลาด: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-2xl text-gray-500 mb-4">{t('cart.emptyMessage')}</p>
            <button
              onClick={() => navigate('/marketplace')}
              className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              {t('cart.continueShopping')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 pb-24 md:pb-8">
      <div className="container mx-auto px-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 md:mb-6">{t('cart.title')}</h1>

        {/* Progress Bar */}
        <div className="mb-6 bg-white rounded-lg shadow-md p-4 md:p-6">
          <CartProgressBar 
            currentStep={currentStep} 
            onStepClick={handleStepClick}
          />
        </div>

        {/* Step 1: Select Items */}
        {currentStep === 1 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">🛒 เลือกสินค้าที่ต้องการสั่งซื้อ</h2>
            
            {/* Select All */}
            <div className="mb-4 flex items-center gap-2 p-4 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                checked={selectedItems.size === cart.length && cart.length > 0}
                onChange={toggleAllSelection}
                className="w-5 h-5 text-green-600 rounded"
              />
              <label className="font-semibold text-gray-800">
                เลือกทั้งหมด ({selectedItems.size}/{cart.length})
              </label>
            </div>

            <div className="space-y-4">
              {cart.map(item => (
                <div key={item.productId} className="flex gap-4 p-4 border-2 rounded-lg hover:border-green-300 transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedItems.has(item.productId)}
                    onChange={() => toggleItemSelection(item.productId)}
                    className="w-5 h-5 text-green-600 rounded mt-1"
                  />
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.productName}
                      className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-lg"
                    />
                  )}
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-gray-800 mb-1">{item.productName}</h4>
                    <p className="text-gray-600 text-sm mb-2">{t('cart.price')}: ฿{item.price?.toLocaleString()} / {item.unit}</p>
                    
                    <div className="flex items-center gap-4 mt-2">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateCartQuantity(item.productId, item.quantity - 1)}
                          className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded flex items-center justify-center"
                        >
                          -
                        </button>
                        <span className="w-12 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateCartQuantity(item.productId, item.quantity + 1)}
                          className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded flex items-center justify-center"
                        >
                          +
                        </button>
                      </div>
                      <p className="text-lg font-semibold text-green-600">
                        ฿{item.subtotal?.toLocaleString()}
                      </p>
                      <button
                        onClick={() => removeFromCart(item.productId)}
                        className="ml-auto text-red-500 hover:text-red-700 text-sm"
                      >
                        🗑️ ลบ
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2-5: Show grouped items */}
        {currentStep >= 2 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {Object.entries(groupedItems).map(([key, group]) => (
                <div key={key} className="bg-white rounded-lg shadow-md p-6 border-2 border-green-200">
                  {/* Store Header */}
                  <div className="mb-4 pb-4 border-b">
                    <h3 className="text-lg font-bold text-gray-800">🏪 {group.store?.name || 'ไม่ทราบร้าน'}</h3>
                    <p className="text-sm text-gray-600">
                      {group.items.length} {t('common.items')} | 
                      {t('cart.paymentMethod')}: {group.paymentMethod === 'admin' ? t('cart.adminPayment') : t('cart.directPayment')}
                    </p>
                  </div>

                  {/* Step 2: Payment Method */}
                  {currentStep === 2 && (
                    <>
                      {/* Items Summary */}
                      <div className="mb-4 space-y-2">
                        {group.items.map(item => (
                          <div key={item.productId} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                            <span className="text-sm text-gray-700">{item.productName} x {item.quantity}</span>
                            <span className="text-sm font-semibold text-green-600">฿{item.subtotal?.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>

                      {/* Payment Method Selection */}
                      <div className="space-y-3">
                        {group.items.map(item => (
                          <div key={item.productId} className="p-4 bg-gray-50 rounded-lg">
                            <label className="block text-sm font-semibold text-gray-700 mb-3">
                              💳 วิธีการชำระเงิน: {item.productName}
                            </label>
                            <div className="flex flex-col sm:flex-row gap-3">
                              <label className="flex items-center gap-2 cursor-pointer p-3 border-2 rounded-lg hover:bg-white transition-colors flex-1">
                                <input
                                  type="radio"
                                  name={`payment-${item.productId}`}
                                  value="direct"
                                  checked={itemPaymentMethods[item.productId] === 'direct'}
                                  onChange={(e) => setItemPaymentMethods(prev => ({ ...prev, [item.productId]: e.target.value }))}
                                  className="w-4 h-4 text-green-600"
                                />
                                <div className="flex-1">
                                  <span className="text-sm font-medium">จ่ายกันเอง</span>
                                  <p className="text-xs text-gray-500">โอนเงินตรงกับร้านค้า</p>
                                </div>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer p-3 border-2 rounded-lg hover:bg-white transition-colors flex-1">
                                <input
                                  type="radio"
                                  name={`payment-${item.productId}`}
                                  value="admin"
                                  checked={itemPaymentMethods[item.productId] === 'admin'}
                                  onChange={(e) => setItemPaymentMethods(prev => ({ ...prev, [item.productId]: e.target.value }))}
                                  className="w-4 h-4 text-green-600"
                                />
                                <div className="flex-1">
                                  <span className="text-sm font-medium">{t('cart.adminPayment')}</span>
                                  <p className="text-xs text-gray-500">+5% ค่าธรรมเนียม</p>
                                </div>
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Store Payment Info */}
                      {group.store?.payment && (
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <h4 className="text-sm font-semibold text-gray-800 mb-3">{t('cart.paymentInfo')}</h4>
                          <PaymentMethodsDisplay 
                            payment={group.store.payment} 
                            productPrice={calculateCartFees(group.items).subtotal}
                          />
                        </div>
                      )}
                    </>
                  )}

                  {/* Step 3: Payment Proof */}
                  {currentStep === 3 && group.paymentMethod === 'direct' && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">📎 แนบหลักฐานการโอน</h4>
                      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">{t('cart.amountToPay')}: <span className="font-semibold text-green-600"> ฿{calculateCartFees(group.items).subtotal.toLocaleString()}</span></p>
                        <p className="text-xs text-gray-500">{t('cart.paymentInstruction')}</p>
                      </div>
                      
                      {itemPaymentProofs[group.items[0]?.productId] ? (
                        <div className="space-y-2">
                          <PaymentProofViewer 
                            paymentProofUrl={itemPaymentProofs[group.items[0].productId]} 
                            alt="Payment Proof"
                            showLabel={true}
                          />
                          <button
                            onClick={() => setItemPaymentProofs(prev => {
                              const newProofs = { ...prev };
                              delete newProofs[group.items[0].productId];
                              return newProofs;
                            })}
                            className="text-sm text-red-600 hover:text-red-700 underline"
                          >
                            ลบและอัพโหลดใหม่
                          </button>
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handlePaymentProofChange(e, group.items[0]?.productId)}
                            className="hidden"
                            id={`payment-proof-${key}`}
                          />
                          <label
                            htmlFor={`payment-proof-${key}`}
                            className="cursor-pointer block"
                          >
                            <div className="text-4xl mb-2">📤</div>
                            <div className="text-gray-600 font-medium mb-1">คลิกเพื่ออัพโหลดสลิป</div>
                            <div className="text-xs text-gray-400">รองรับไฟล์รูปภาพ (JPG, PNG)</div>
                          </label>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 3: Admin Middleman Info */}
                  {currentStep === 3 && group.paymentMethod === 'admin' && (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="text-lg font-semibold text-gray-800 mb-2">✅ {t('cart.adminMiddleman')}</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        {t('cart.total')}: <span className="font-semibold">฿{calculateCartFees(group.items).subtotal.toLocaleString()}</span>
                      </p>
                      <p className="text-sm text-gray-600 mb-2">
                        ค่าธรรมเนียม 5%: <span className="font-semibold text-red-600">+฿{Math.ceil(calculateCartFees(group.items).subtotal * 0.05).toLocaleString()}</span>
                      </p>
                      <p className="text-sm text-gray-600">
                        {t('cart.totalAll')}: <span className="font-semibold text-green-600">฿{(calculateCartFees(group.items).subtotal + Math.ceil(calculateCartFees(group.items).subtotal * 0.05)).toLocaleString()}</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-2">ระบบจะหักค่าธรรมเนียมจากเหรียญอัตโนมัติ</p>
                    </div>
                  )}

                  {/* Step 4: Shipping */}
                  {currentStep === 4 && (
                    <>
                      {/* Shipping Method Selection */}
                      {group.store?.shipping && group.store.shipping.methods && group.store.shipping.methods.length > 0 && (
                        <div className="mb-4">
                          <label className="block text-sm font-semibold text-gray-700 mb-3">🚚 วิธีการจัดส่ง</label>
                          <div className="space-y-2">
                            {group.store.shipping.methods.includes('courier') && (
                              <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                <input
                                  type="radio"
                                  name={`shipping-${key}`}
                                  value="courier"
                                  checked={itemShippingMethods[group.items[0]?.productId] === 'courier'}
                                  onChange={(e) => setItemShippingMethods(prev => ({ ...prev, [group.items[0].productId]: e.target.value }))}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-gray-700">📦 บริษัทขนส่ง</div>
                                  {group.store.shipping.courierCompanies && (
                                    <div className="text-xs text-gray-500 mt-1">({group.store.shipping.courierCompanies.join(', ')})</div>
                                  )}
                                </div>
                              </label>
                            )}
                            {group.store.shipping.methods.includes('pickup') && (
                              <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                <input
                                  type="radio"
                                  name={`shipping-${key}`}
                                  value="pickup"
                                  checked={itemShippingMethods[group.items[0]?.productId] === 'pickup'}
                                  onChange={(e) => setItemShippingMethods(prev => ({ ...prev, [group.items[0].productId]: e.target.value }))}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-gray-700">📍 นัดรับ (ฟรี)</div>
                                </div>
                              </label>
                            )}
                            {group.store.shipping.methods.includes('special_vehicle') && (
                              <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                <input
                                  type="radio"
                                  name={`shipping-${key}`}
                                  value="special_vehicle"
                                  checked={itemShippingMethods[group.items[0]?.productId] === 'special_vehicle'}
                                  onChange={(e) => setItemShippingMethods(prev => ({ ...prev, [group.items[0].productId]: e.target.value }))}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-gray-700">🚛 ฝากส่งกับรถพิเศษ</div>
                                </div>
                              </label>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Shipping Address */}
                      <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">📍 ที่อยู่จัดส่ง *</label>
                        <textarea
                          value={itemShippingAddresses[key] || ''}
                          onChange={(e) => setItemShippingAddresses(prev => ({ ...prev, [key]: e.target.value }))}
                          rows="4"
                          required
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          placeholder="กรุณาใส่ที่อยู่จัดส่งที่ชัดเจน (บ้านเลขที่, ถนน, ตำบล, อำเภอ, จังหวัด, รหัสไปรษณีย์)"
                        />
                      </div>

                      {/* Buyer Phone */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          📞 เบอร์โทรผู้รับ * 
                          <span className="text-xs text-gray-500 ml-2">(สำหรับ QR Code บนใบส่งของ)</span>
                        </label>
                        <input
                          type="tel"
                          value={itemBuyerPhones[key] || ''}
                          onChange={(e) => setItemBuyerPhones(prev => ({ ...prev, [key]: e.target.value }))}
                          required
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          placeholder="08X-XXX-XXXX"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          บริษัทขนส่งสามารถ scan QR Code เพื่อค้นหาลูกค้าได้
                        </p>
                      </div>
                    </>
                  )}

                  {/* Step 5: Review & Confirm */}
                  {currentStep === 5 && (
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">📋 สรุปออเดอร์</h4>
                      
                      {/* Items Summary */}
                      <div className="space-y-2">
                        {group.items.map(item => (
                          <div key={item.productId} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                            <div>
                              <span className="text-sm font-medium text-gray-800">{item.productName}</span>
                              <span className="text-xs text-gray-500 ml-2">x {item.quantity}</span>
                            </div>
                            <span className="text-sm font-semibold text-green-600">฿{item.subtotal?.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>

                      {/* Payment Method */}
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">{t('cart.paymentMethod')}:</span> {group.paymentMethod === 'admin' ? `${t('cart.adminPayment')} (+5%)` : t('cart.directPayment')}
                        </p>
                        {group.paymentMethod === 'direct' && itemPaymentProofs[group.items[0]?.productId] && (
                          <p className="text-xs text-green-600 mt-1">✅ แนบหลักฐานการโอนแล้ว</p>
                        )}
                      </div>

                      {/* Shipping */}
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-700 mb-2">
                          <span className="font-semibold">วิธีการจัดส่ง:</span> {
                            itemShippingMethods[group.items[0]?.productId] === 'courier' ? '📦 บริษัทขนส่ง' :
                            itemShippingMethods[group.items[0]?.productId] === 'pickup' ? '📍 นัดรับ' :
                            itemShippingMethods[group.items[0]?.productId] === 'special_vehicle' ? '🚛 รถพิเศษ' : '-'
                          }
                        </p>
                        {itemShippingAddresses[key] && (
                          <p className="text-xs text-gray-600 mt-1">
                            <span className="font-semibold">ที่อยู่:</span> {itemShippingAddresses[key]}
                          </p>
                        )}
                        {itemBuyerPhones[key] && (
                          <p className="text-xs text-gray-600 mt-1">
                            <span className="font-semibold">เบอร์โทร:</span> {itemBuyerPhones[key]}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Order Summary - Only show in Step 1 and Step 5 */}
            {(currentStep === 1 || currentStep === 5) && (
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">สรุปออเดอร์</h2>
              
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-gray-600">
                  <span>{t('cart.totalItems')}:</span>
                  <span>฿{selectedTotal.subtotal?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>ค่าธรรมเนียม:</span>
                  <span>{selectedTotal.fee} เหรียญ</span>
                </div>
                
                {/* Calculate admin fees */}
                {Object.entries(groupedItems).map(([key, group]) => {
                  const groupTotal = calculateCartFees(group.items);
                  const adminFee = group.paymentMethod === 'admin' ? Math.ceil(groupTotal.subtotal * 0.05) : 0;
                  if (adminFee > 0) {
                    return (
                      <div key={key} className="text-xs text-red-600">
                        ค่าธรรมเนียมแอดมิน ({group.store?.name}): +{adminFee.toLocaleString()} บาท
                      </div>
                    );
                  }
                  return null;
                })}
                
                <div className="border-t pt-2 flex justify-between text-lg font-bold text-gray-800">
                  <span>{t('cart.total')}:</span>
                  <span>
                    ฿{(
                      selectedTotal.subtotal + 
                      Object.entries(groupedItems).reduce((sum, [key, group]) => {
                        const groupTotal = calculateCartFees(group.items);
                        const adminFee = group.paymentMethod === 'admin' ? Math.ceil(groupTotal.subtotal * 0.05) : 0;
                        return sum + adminFee;
                      }, 0)
                    ).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Important Notice */}
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <span className="text-yellow-600 text-lg">⚠️</span>
                  <div className="text-xs text-yellow-800">
                    <p className="font-semibold mb-1">{t('cart.directPaymentNote')}</p>
                    <p>ระบบคิดแค่ค่าธรรมเนียม</p>
                  </div>
                </div>
              </div>

              {currentUser && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    เหรียญที่มี: <span className="font-semibold">{coins}</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    ค่าธรรมเนียมที่ต้องใช้: <span className="font-semibold">{selectedTotal.fee}</span> เหรียญ
                  </p>
                </div>
              )}

                  {/* Step 5: Show checkout button */}
                  {currentStep === 5 && (
                    <button
                      onClick={handleCheckout}
                      disabled={loading || selectedItems.size === 0}
                      className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                    >
                      {loading ? 'กำลังสร้างออเดอร์...' : `ยืนยันสั่งซื้อ (${Object.keys(groupedItems).length} รายการ)`}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Navigation Buttons - Fixed at bottom on mobile */}
        <div className="fixed bottom-0 left-0 right-0 md:relative md:mt-6 bg-white border-t md:border-0 shadow-lg md:shadow-none p-4 md:p-0 z-50">
          <div className="container mx-auto px-4">
            <div className="flex gap-3">
              {currentStep > 1 && (
                <button
                  onClick={handleBackStep}
                  className="flex-1 md:flex-none md:px-8 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-lg transition-colors"
                >
                  ← กลับ
                </button>
              )}
              {currentStep < 5 ? (
                <button
                  onClick={handleNextStep}
                  className="flex-1 md:flex-none md:px-8 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                >
                  ถัดไป →
                </button>
              ) : (
                <button
                  onClick={handleCheckout}
                  disabled={loading || selectedItems.size === 0}
                  className="flex-1 md:flex-none md:px-8 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                >
                  {loading ? 'กำลังสร้างออเดอร์...' : `ยืนยันสั่งซื้อ (${Object.keys(groupedItems).length} รายการ)`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;



